/**
 * Plan Entitlements Configuration (FALLBACK)
 * ===========================================
 * 
 * IMPORTANT: This is a FALLBACK only. The canonical source of truth is:
 * GET /api/entitlements/config
 * 
 * Use useEntitlementsConfig() hook or fetchEntitlementsConfig() for live data.
 * 
 * These values MUST match backend config/entitlements_config.py
 */

export const PLAN_ENTITLEMENTS = {
  free: {
    name: "Free",
    price: null,
    priceDisplay: "Free",
    loungeMinutesPerDay: 20,         // Backend: LOUNGE_FREE_MINUTES_PER_DAY=20
    speedDatingMinutesPerMonth: 30,  // Backend: SPEED_FREE_MINUTES_PER_MONTH=30
    unlimitedMessaging: false,
    aiWingman: false,
    seeWhoLikedYou: false,
    verifiedBadge: false,
    priorityMatching: false,
    profileBoosts: 0,
  },
  
  inner_circle: {
    name: "Inner Circle",
    price: 14.99,
    priceDisplay: "$14.99",
    period: "/month",
    loungeMinutesPerDay: 40,          // Backend: LOUNGE_INNER_CIRCLE_MINUTES_PER_DAY=40
    speedDatingMinutesPerMonth: 360,  // Backend: SPEED_INNER_CIRCLE_MINUTES_PER_MONTH=360
    unlimitedMessaging: false,
    aiWingman: false,
    seeWhoLikedYou: false,
    verifiedBadge: true,
    priorityMatching: true,
    profileBoosts: 0,
    innerCircleBadge: true,
    exclusiveEvents: true,
  },
  
  elite: {
    name: "Elite",
    price: 19.99,
    priceDisplay: "$19.99",
    period: "/month",
    loungeMinutesPerDay: 60,          // Backend: LOUNGE_ELITE_MINUTES_PER_DAY=60
    speedDatingMinutesPerMonth: 240,  // Backend: SPEED_ELITE_MINUTES_PER_MONTH=240
    unlimitedMessaging: false,  // No tier has "unlimited" - all have limits
    aiWingman: true,
    seeWhoLikedYou: true,
    verifiedBadge: true,
    priorityMatching: true,
    profileBoosts: 4,
  },
  
  elite_plus: {
    name: "Elite+",
    price: 39.99,
    priceDisplay: "$39.99",
    period: "/month",
    loungeMinutesPerDay: 120,         // Backend: LOUNGE_ELITE_PLUS_MINUTES_PER_DAY=120
    speedDatingMinutesPerMonth: 240,  // Backend: SAME AS ELITE (NOT unlimited!)
    unlimitedMessaging: false,  // No tier has "unlimited" - all have limits
    aiWingman: true,
    seeWhoLikedYou: true,
    verifiedBadge: true,
    priorityMatching: true,
    profileBoosts: 8,
  },
};

/**
 * Helper to format minutes as human-readable time
 * @param {number} minutes - Minutes (0 for none)
 * @returns {string} Formatted time string
 */
export function formatMinutes(minutes) {
  if (minutes === 0) return "None";
  if (minutes < 60) return `${minutes} min`;
  if (minutes === 60) return "1 hour";
  if (minutes % 60 === 0) return `${minutes / 60} hours`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

/**
 * Format lounge time for display
 * @param {number} minutesPerDay 
 * @returns {string}
 */
export function formatLoungeTime(minutesPerDay) {
  if (minutesPerDay === 0) return "Not included";
  return `${formatMinutes(minutesPerDay)}/day`;
}

/**
 * Format speed dating time for display
 * @param {number} minutesPerMonth 
 * @returns {string}
 */
export function formatSpeedDatingTime(minutesPerMonth) {
  if (minutesPerMonth === 0) return "Not included";
  return `${formatMinutes(minutesPerMonth)}/month`;
}

/**
 * Get plan by key
 * @param {string} planKey - 'free' | 'inner_circle' | 'elite' | 'elite_plus'
 * @returns {object} Plan config
 */
export function getPlanConfig(planKey) {
  return PLAN_ENTITLEMENTS[planKey] || PLAN_ENTITLEMENTS.free;
}

export default PLAN_ENTITLEMENTS;
